import { createSelector } from '@reduxjs/toolkit';

import { ComplianceTypes, RegionToComplianceType } from '@/constants';
import { ComplianceType, SessionSummary } from '@/types';

import { selectAppRegion } from '../app-settings';
import { selectSessionUserId } from '../session';
import { RootState } from '../store';
import { selectPlan } from '../user-api';
import { playerFsmApi, playerSessionsAdapter } from './player-fsm-api.slice';
import {
  getSessionComplianceType,
  pluckIdsOfStatus,
} from './player-fsm-api.utils';

export const selectPlayerSessionsResult =
  playerFsmApi.endpoints.fetchPlayerSessions.select();

export const selectPlayerSessionsData = createSelector(
  selectPlayerSessionsResult,
  playerSessionsResult =>
    playerSessionsResult?.data ?? playerSessionsAdapter.getInitialState(),
);

const playerSessionsEntitySelectors = playerSessionsAdapter.getSelectors(
  selectPlayerSessionsData,
);
export const playerSessionsSelectors = {
  ...playerSessionsEntitySelectors,
  selectById: (state: RootState, id: string) =>
    playerSessionsEntitySelectors.selectById(state, id) as
      | SessionSummary
      | undefined,
};

const selectPlayerSessionsSortedByLastPlayed = createSelector(
  playerSessionsSelectors.selectAll,
  sessions => {
    return sessions.sort((a, b) => {
      return new Date(b.lastEvent).getTime() - new Date(a.lastEvent).getTime();
    });
  },
);
export const selectPlayerConfigResult =
  playerFsmApi.endpoints.fetchPlayerConfig.select();

export const selectUserCompliance = createSelector(
  selectSessionUserId,
  selectPlayerConfigResult,
  selectAppRegion,
  (userId, { data: playerConfig }, appRegion) => {
    if (!userId || !playerConfig) return RegionToComplianceType[appRegion];
    return playerConfig.defaultPlayer;
  },
);

export const selectPlayerRules = createSelector(
  selectPlayerConfigResult,
  ({ data: playerConfig }) => playerConfig?.rules ?? {},
);

export const selectBookmarkedEpisodeIds = createSelector(
  playerSessionsSelectors.selectAll,
  sessions => {
    return sessions
      .filter(session => !!session.extraStates?.bookmark)
      .sort((a, b) => {
        if (
          !a.extraStates?.bookmark?.bookmarkedAt ||
          !b.extraStates?.bookmark?.bookmarkedAt
        )
          return 0;
        return (
          new Date(b.extraStates.bookmark.bookmarkedAt).getTime() -
          new Date(a.extraStates.bookmark.bookmarkedAt).getTime()
        );
      })
      .map(session => session.episodeId);
  },
);

export const selectEpisodeCompliance = createSelector(
  (state: RootState, episodeId?: string) =>
    episodeId
      ? playerSessionsSelectors.selectById(state, episodeId)
      : undefined,
  selectUserCompliance,
  (
    playerSession: SessionSummary | undefined,
    complianceType: ComplianceType,
  ) =>
    playerSession?.fsmType
      ? getSessionComplianceType(playerSession)
      : complianceType,
);

export const selectIsEpisodeRedeemRequired = createSelector(
  selectSessionUserId,
  selectPlan,
  (state: RootState, episodeId: string) =>
    selectEpisodeCompliance(state, episodeId),
  (state: RootState, episodeId: string) =>
    playerSessionsSelectors.selectById(state, episodeId),
  (userId, { data: plan }, compliance, playerSession) => {
    if (!userId) return false;
    if (compliance !== ComplianceTypes.CPE) return false;
    if (playerSession?.status === 'verified') return false;
    if (playerSession?.isRedeemed) return false;
    if (!plan?.licenceValid) return true;
    if (!plan.showUsageRemaining) return false;
    return true;
  },
);

export const selectInProgressEpisodeIds = createSelector(
  [selectPlayerSessionsSortedByLastPlayed, selectUserCompliance],
  (sessions, compliance) => {
    const ongoingIds = pluckIdsOfStatus('onGoing')(sessions);
    if (compliance !== ComplianceTypes.CPE) return ongoingIds;
    const quizReadyIds = pluckIdsOfStatus('quizReady')(sessions);
    return [...ongoingIds, ...quizReadyIds];
  },
);

export const selectCompletedEpisodeIds = createSelector(
  selectPlayerSessionsSortedByLastPlayed,
  sessions => pluckIdsOfStatus('verified')(sessions),
);

export const selectAssessmentReadyEpisodeIds = createSelector(
  selectPlayerSessionsSortedByLastPlayed,
  sessions => pluckIdsOfStatus('assessmentReady')(sessions),
);

export const selectQuizReadyEpisodeData = createSelector(
  selectPlayerSessionsSortedByLastPlayed,
  sessions =>
    sessions
      .filter(session =>
        session.chapterStates?.some((state: string) => state === 'quiz_ready'),
      )
      .reduce(
        (acc, session) => ({
          ...acc,
          [session.episodeId]: session.availableQuizCount,
        }),
        {} as { [episodeId: string]: number },
      ),
);

export const selectCreditEarnedSessions = createSelector(
  [selectPlayerSessionsSortedByLastPlayed, selectUserCompliance],
  (sessions, compliance) => {
    if (compliance === ComplianceTypes.CPD) {
      return sessions.filter(
        session =>
          session.state !== 'episode_archived' &&
          session.chapterStates?.some((state: string) => state === 'verified'),
      );
    }
    return sessions.filter(
      session =>
        session.state !== 'episode_archived' &&
        session.extraStates?.creditState === 'earned',
    );
  },
);
